/**
 * Shared campaigns management UI — used both inside the Admin tabs and
 * as a standalone page (`/campaigns`) accessible to ops role.
 *
 * Uses require_ops_or_admin on the backend, so ops users can CRUD campaigns
 * without needing admin rights.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { notifyLaafficChanged } from "@/components/laaffic/LaafficUnmappedBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Tag } from "lucide-react";
import PromoConfigDialog from "./PromoConfigDialog";
import { useAuth } from "@/lib/auth";

export default function CampaignsManager() {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCampaign, setPromoCampaign] = useState(null);
  const [form, setForm] = useState({
    name: "", brand_id: "", report_type: "FTD", team_id: "", is_active: true,
    min_inactive_days: 0, allowed_period_after_call: 0,
  });

  const load = () => api.get("/admin/campaigns")
    .then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));

  useEffect(() => {
    load();
    // /brands is public; /teams is admin-only but ops-writer role also has it
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "", brand_id: "", report_type: "FTD", team_id: "", is_active: true,
      min_inactive_days: 0, allowed_period_after_call: 7, max_lookback_days: 0,
      laaffic_voice_group_id: "", laaffic_sip_group_name: "",
      laaffic_task_id: "", laaffic_task_name: "",
    });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, brand_id: String(c.brand_id), report_type: c.report_type,
      team_id: c.team_id, is_active: c.is_active,
      min_inactive_days: c.min_inactive_days || 0,
      allowed_period_after_call: c.allowed_period_after_call || 0,
      max_lookback_days: c.max_lookback_days || 0,
      laaffic_voice_group_id: c.laaffic_voice_group_id ?? "",
      laaffic_sip_group_name: c.laaffic_sip_group_name || "",
      laaffic_task_id: c.laaffic_task_id || "",
      laaffic_task_name: c.laaffic_task_name || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = {
        name: form.name.trim(),
        brand_id: parseInt(form.brand_id, 10),
        report_type: form.report_type,
        team_id: form.team_id,
        is_active: form.is_active,
        min_inactive_days: form.report_type === "FTD" ? 0 : parseInt(form.min_inactive_days, 10) || 0,
        allowed_period_after_call: parseInt(form.allowed_period_after_call, 10) || 0,
        max_lookback_days: parseInt(form.max_lookback_days, 10) || 0,
        laaffic_voice_group_id: form.laaffic_voice_group_id === "" ? null : parseInt(form.laaffic_voice_group_id, 10),
        laaffic_sip_group_name: (form.laaffic_sip_group_name || "").trim() || null,
        laaffic_task_id: (form.laaffic_task_id || "").trim() || null,
        laaffic_task_name: (form.laaffic_task_name || "").trim() || null,
      };
      if (!body.name || !body.brand_id || !body.team_id) {
        toast.error("Name, brand and team are required.");
        return;
      }
      if (editing) {
        await api.patch(`/admin/campaigns/${editing.id}`, body);
        toast.success(`Updated campaign "${body.name}"`);
      } else {
        await api.post("/admin/campaigns", body);
        toast.success(`Created campaign "${body.name}"`);
      }
      notifyLaafficChanged();
      setOpen(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? Ops uploads referencing it will start showing "unmatched".`)) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-slate-600 max-w-2xl">
          Configure the mapping <b>Campaign name → Brand + FTD/RD + Team</b>. When you upload an ops file with a <code className="bg-slate-100 px-1 rounded">Campaign</code> column, the app auto-splits and enriches rows against this table — no need to upload one file per brand.
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {isAdmin && (
            <DialogTrigger asChild>
              <Button className="btn-tactile" onClick={openCreate} data-testid="admin-new-campaign-btn"
                style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}>
                <Plus className="w-4 h-4 mr-1" /> New campaign
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editing ? "Edit campaign" : "Create campaign"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Campaign name</Label>
                <Input className="glass-input mt-1" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. F7 FTD" data-testid="campaign-name-input" />
                <p className="text-[11px] text-slate-500 mt-1">Must match the &quot;Campaign&quot; column in the ops file (case-insensitive).</p>
              </div>
              <div>
                <Label>Brand</Label>
                <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                  <SelectTrigger className="glass-input mt-1" data-testid="campaign-brand-select"><SelectValue placeholder="Select brand" /></SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => <SelectItem key={b.brand_id} value={String(b.brand_id)}>{b.brand_name} <span className="text-slate-400">#{b.brand_id}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Report type</Label>
                <div className="flex gap-2 mt-1">
                  {["FTD", "RD"].map((rt) => (
                    <button key={rt}
                      onClick={() => setForm({ ...form, report_type: rt })}
                      data-testid={`campaign-report-type-${rt}`}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${form.report_type === rt ? (rt === "FTD" ? "bg-emerald-100 text-emerald-800 ring-2 ring-emerald-200" : "bg-blue-100 text-blue-800 ring-2 ring-blue-200") : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {rt === "FTD" ? "FTD (First-time deposit)" : "RD (Re-deposit)"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Team</Label>
                <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                  <SelectTrigger className="glass-input mt-1" data-testid="campaign-team-select"><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>
                    Deposit window <span className="text-slate-400 font-normal">(days after call)</span>
                  </Label>
                  <Input
                    type="number" min="0" max="365"
                    value={form.allowed_period_after_call}
                    onChange={(e) => setForm({ ...form, allowed_period_after_call: e.target.value })}
                    className="glass-input mt-1 font-mono text-center"
                    data-testid="campaign-allowed-period-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Deposits later than this don&apos;t credit the agent. Set 0 = no limit.</p>
                </div>
                <div>
                  <Label>
                    Min inactive days <span className="text-slate-400 font-normal">(before call)</span>
                  </Label>
                  <Input
                    type="number" min="0" max="3650"
                    value={form.report_type === "FTD" ? 0 : form.min_inactive_days}
                    onChange={(e) => setForm({ ...form, min_inactive_days: e.target.value })}
                    disabled={form.report_type === "FTD"}
                    className="glass-input mt-1 font-mono text-center disabled:opacity-50"
                    data-testid="campaign-min-inactive-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    {form.report_type === "FTD"
                      ? "N/A for FTD (customer hasn\u2019t deposited yet)"
                      : "Customer must have been inactive at least this many days before the call. Set 0 to disable."}
                  </p>
                </div>
              </div>
              <div>
                <Label>
                  Leads lookback window <span className="text-slate-400 font-normal">(max days back)</span>
                </Label>
                <Input
                  type="number" min="0" max="3650"
                  value={form.max_lookback_days}
                  onChange={(e) => setForm({ ...form, max_lookback_days: e.target.value })}
                  className="glass-input mt-1 font-mono text-center"
                  data-testid="campaign-max-lookback-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  {form.report_type === "FTD"
                    ? "FTD: include users registered within last N days. Example: 2 = last 48 hours only. 0 = no upper bound."
                    : "RD: include users whose last deposit falls within last N days. Example: 365 = users inactive for min-days to 365 days. 0 = no upper bound."}
                </p>
              </div>
              {/* Laaffic agent-group mapping — routes inbound CDRs to
                  this campaign. Laaffic's CDR feed does NOT expose a
                  task_id; the deterministic identifier is voiceGroupId
                  (a.k.a. Agent Group ID) with sipGroupName as a
                  human-readable hint. */}
              <div className="border border-indigo-100 rounded-lg p-3 bg-indigo-50/30 space-y-2">
                <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-medium">
                  Laaffic agent group mapping
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Laaffic Agent Group ID *</Label>
                    <Input
                      type="number"
                      value={form.laaffic_voice_group_id}
                      onChange={(e) => setForm({ ...form, laaffic_voice_group_id: e.target.value })}
                      placeholder="e.g. 5008104"
                      className="glass-input mt-1 font-mono text-xs"
                      data-testid="campaign-laaffic-group-id-input"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      From Laaffic console → Agent Group → <b>Agent Group ID</b> column.
                      This is the deterministic key that routes inbound CDRs to this campaign.
                    </p>
                  </div>
                  <div>
                    <Label>SIP / Group name <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input
                      value={form.laaffic_sip_group_name}
                      onChange={(e) => setForm({ ...form, laaffic_sip_group_name: e.target.value })}
                      placeholder="e.g. Nola_F7_BD"
                      className="glass-input mt-1 font-mono text-xs"
                      data-testid="campaign-laaffic-sip-group-input"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Human-readable Laaffic group name — display only, helps operators recognise the link.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  data-testid="campaign-active-toggle" />
                <Label className="!m-0">Active</Label>
                <span className="text-[11px] text-slate-500">Inactive campaigns are ignored during auto-detect uploads.</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="campaign-save-btn">{editing ? "Save changes" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass-card p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="campaigns-table">
          <thead>
            <tr className="text-slate-500 text-xs uppercase">
              <th className="text-left py-2 px-3">Campaign name</th>
              <th className="text-left py-2 px-3">Brand</th>
              <th className="text-left py-2 px-3">Type</th>
              <th className="text-left py-2 px-3">Team</th>
              <th className="text-right py-2 px-3" title="Days after the call within which a deposit still counts toward credit. 0 = no limit.">Window ↦</th>
              <th className="text-right py-2 px-3" title="Customer must have been inactive at least this many days before the call to earn credit. 0 = disabled.">Cooldown ↤</th>
              <th className="text-right py-2 px-3" title="Leads generation lookback. FTD: users registered within last N days. RD: users whose last deposit is within last N days. 0 = no upper bound.">Lookback ⤶</th>
              <th className="text-left py-2 px-3" title="Laaffic Agent Group ID (numeric voiceGroupId) — deterministic matching key that routes inbound CDRs to this campaign. SIP name below is the human-readable Laaffic group name.">Laaffic group</th>
              <th className="text-left py-2 px-3">Active</th>
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><td colSpan={10} className="py-8 text-center text-slate-500 text-sm">
                No campaigns yet. Add one so ops uploads can auto-route rows.
              </td></tr>
            )}
            {campaigns.map((c) => {
              const brand = brands.find((b) => b.brand_id === c.brand_id);
              const team = teams.find((t) => t.id === c.team_id);
              return (
                <tr key={c.id} className="border-t border-white/60 hover:bg-white/50">
                  <td className="py-2 px-3 font-mono font-medium">{c.name}</td>
                  <td className="py-2 px-3">{brand?.brand_name || `#${c.brand_id}`}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.report_type === "FTD" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                      {c.report_type}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-600">{team?.name || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    {c.allowed_period_after_call ? `${c.allowed_period_after_call}d` : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    {c.report_type === "FTD" ? <span className="text-slate-400">N/A</span> :
                      (c.min_inactive_days ? `${c.min_inactive_days}d` : <span className="text-slate-400">—</span>)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    {c.max_lookback_days ? `${c.max_lookback_days}d` : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2 px-3 font-mono text-[11px] leading-tight"
                      data-testid={`campaign-laaffic-${c.id}`}>
                    {c.laaffic_voice_group_id != null ? (
                      <div>
                        <div className="text-indigo-700 font-semibold" title="voiceGroupId — matches inbound CDRs to this campaign">
                          {c.laaffic_voice_group_id}
                        </div>
                        {c.laaffic_sip_group_name && (
                          <div className="text-[10px] text-slate-500" title="sipGroupName — human name in Laaffic">
                            {c.laaffic_sip_group_name}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <Switch checked={c.is_active}
                      onCheckedChange={async (v) => {
                        try {
                          await api.patch(`/admin/campaigns/${c.id}`, { is_active: v });
                          load();
                        } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
                      }}
                      data-testid={`campaign-active-${c.id}`} />
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => { setPromoCampaign(c); setPromoOpen(true); }}
                          className="text-emerald-600 text-xs mr-3 inline-flex items-center gap-1"
                          data-testid={`campaign-promo-${c.id}`}
                          title="Manage promo codes for this campaign"
                        >
                          <Tag className="w-3 h-3" /> Promo
                        </button>
                        <button onClick={() => openEdit(c)} className="text-[#0A84FF] text-xs mr-3" data-testid={`campaign-edit-${c.id}`}>Edit</button>
                        <button onClick={() => remove(c)} className="text-red-500" data-testid={`campaign-delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {!isAdmin && (
                      <span className="text-xs text-slate-400">Read-only</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PromoConfigDialog
        open={promoOpen}
        campaign={promoCampaign}
        onClose={() => { setPromoOpen(false); setPromoCampaign(null); }}
      />
    </div>
  );
}
